import { ArrowRight, Cpu, ExternalLink, FileText, MailPlus, Mic } from "lucide-react"
import { router } from "@inertiajs/react"
import { FormEvent, useState } from "react"

type City = {
  id: number
  name: string
  slug: string
  report_count: number
  last_report_at: string | null
}

type CityGroup = {
  country_code: string
  country_name: string
  cities: City[]
}

type Props = {
  city_groups: CityGroup[]
}

export default function CitiesIndex({ city_groups }: Props) {
  const [email, setEmail] = useState("")
  const [citySlug, setCitySlug] = useState("")
  const [formError, setFormError] = useState("")

  const cities = city_groups.flatMap((group) => group.cities)

  const handleSubscribe = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!citySlug) {
      setFormError("Select city edition before subscribing.")
      return
    }

    setFormError("")
    router.post(`/cities/${citySlug}/subscriptions`, { email })
  }

  return (
    <main className="ch-noise relative z-10 flex-grow selection:bg-[hsl(var(--ch-primary))] selection:text-white">
      <section id="subscribe" className="ch-container border-b border-[hsl(var(--ch-primary))] py-12 md:py-20">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
          <div className="border-b border-[hsl(var(--ch-primary))] pb-8 md:col-span-7 md:border-b-0 md:border-r md:pb-0 md:pr-8">
            <h1 className="ch-masthead mb-6 text-[hsl(var(--ch-primary))]">YOUR NEIGHBORHOOD, NOTED.</h1>
            <p className="ch-body-lg mb-8 max-w-xl text-[hsl(var(--ch-muted))]">
              Get the facts on local decisions that affect your life. We track municipal meetings to keep you
              informed on tax spending, zoning changes, and infrastructure projects in your backyard.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-[hsl(var(--ch-primary))]">
              <span className="ch-label-caps border-b border-[hsl(var(--ch-primary))] pb-1">Daily Dispatches</span>
              <span className="h-1 w-1 bg-[hsl(var(--ch-primary))]" />
              <span className="ch-label-caps border-b border-[hsl(var(--ch-primary))] pb-1">Unbiased Ledger</span>
              <span className="h-1 w-1 bg-[hsl(var(--ch-primary))]" />
              <span className="ch-label-caps border-b border-[hsl(var(--ch-primary))] pb-1">Permanent Archive</span>
            </div>
          </div>

          <div className="pt-2 md:col-span-5 md:pl-8">
            <div className="border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface))] p-8 text-center">
              <MailPlus className="mx-auto mb-4 h-9 w-9 text-[hsl(var(--ch-primary))]" />
              <h2 className="ch-headline-md mb-2 uppercase text-[hsl(var(--ch-primary))]">The Municipal Dispatch</h2>
              <p className="mb-6 text-sm leading-relaxed text-[hsl(var(--ch-muted))]">
                Receive curated summaries of local council decisions that impact your daily life and wallet.
              </p>
              <form className="space-y-4" onSubmit={handleSubscribe}>
                <div className="text-left">
                  <label className="ch-label-caps mb-1 block text-[hsl(var(--ch-primary))]" htmlFor="city-edition">
                    Select city edition
                  </label>
                  <select
                    className="w-full border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-3 text-sm text-[hsl(var(--ch-primary))] focus:outline-none"
                    id="city-edition"
                    onChange={(event) => setCitySlug(event.target.value)}
                    required
                    value={citySlug}
                  >
                    <option value="">Choose a city...</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.slug}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-left">
                  <label className="ch-label-caps mb-1 block text-[hsl(var(--ch-primary))]" htmlFor="email">
                    Electronic Address
                  </label>
                  <input
                    className="w-full border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] p-3 text-sm text-[hsl(var(--ch-primary))] placeholder:text-[hsl(var(--ch-muted))] focus:outline-none"
                    id="email"
                    name="email"
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="citizen@example.com"
                    required
                    type="email"
                    value={email}
                  />
                </div>
                {formError ? <p className="text-left text-sm text-rose-700">{formError}</p> : null}
                <button
                  className="ch-label-caps w-full border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-primary))] py-4 text-white transition-colors hover:bg-[hsl(var(--ch-bg))] hover:text-[hsl(var(--ch-primary))]"
                  type="submit"
                >
                  Subscribe to your city
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section id="find-your-city" className="ch-container border-b border-[hsl(var(--ch-primary))] py-16">
        <div className="mb-10 text-center">
          <h2 className="ch-headline-lg mb-4 uppercase text-[hsl(var(--ch-primary))]">City Index</h2>
        </div>

        <div className="space-y-10">
          {city_groups.map((group) => (
            <section key={group.country_code || group.country_name}>
              <div className="mb-4 flex items-center justify-between border-b border-[hsl(var(--ch-primary))] pb-2">
                <h3 className="ch-headline-md uppercase text-[hsl(var(--ch-primary))]">{group.country_name}</h3>
                <span className="ch-label-caps text-[hsl(var(--ch-muted))]">{group.country_code}</span>
              </div>

              <div className="grid grid-cols-1 gap-0 border border-[hsl(var(--ch-primary))] sm:grid-cols-2 lg:grid-cols-3">
                {group.cities.map((city) => (
                  <a
                    key={city.id}
                    className="group flex min-h-[220px] flex-col justify-between border-b border-r border-[hsl(var(--ch-primary))] p-6 transition-colors hover:bg-[hsl(var(--ch-surface))]"
                    href={`/cities/${city.slug}`}
                  >
                    <div>
                      <span className="ch-label-caps mb-2 block text-[hsl(var(--ch-muted))]">Municipal Desk</span>
                      <h4 className="ch-headline-md uppercase text-[hsl(var(--ch-primary))] group-hover:underline">{city.name}</h4>
                    </div>
                    <div className="space-y-2 text-sm text-[hsl(var(--ch-muted))]">
                      <p>
                        <span className="ch-label-caps mr-2 text-[hsl(var(--ch-primary))]">Reports</span>
                        {city.report_count}
                      </p>
                      <p>
                        <span className="ch-label-caps mr-2 text-[hsl(var(--ch-primary))]">Last Report</span>
                        {city.last_report_at ? new Date(city.last_report_at).toLocaleString() : "No reports yet"}
                      </p>
                      <div className="mt-2 flex items-end justify-between">
                        <span className="text-sm">View city page</span>
                        <ArrowRight className="h-5 w-5 text-[hsl(var(--ch-primary))] transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a
            className="ch-label-caps inline-flex items-center gap-2 border-b border-[hsl(var(--ch-primary))] pb-0.5 text-[hsl(var(--ch-primary))]"
            href="/cities"
          >
            View all municipalities
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section id="how-it-works" className="border-b border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface-low))]">
        <div className="ch-container py-16">
          <h2 className="ch-headline-lg mb-12 border-b border-[hsl(var(--ch-primary))] pb-6 text-center uppercase text-[hsl(var(--ch-primary))]">
            How the archive works
          </h2>

          <div className="grid grid-cols-1 border border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-bg))] md:grid-cols-3">
            {[
              {
                icon: Mic,
                step: "1",
                title: "We monitor",
                copy: "Our systems monitor municipal feeds, capturing every minute of local deliberation."
              },
              {
                icon: Cpu,
                step: "2",
                title: "We summarize",
                copy: "We distill hours of meetings into clear, actionable reports on the issues that matter most to residents."
              },
              {
                icon: FileText,
                step: "3",
                title: "You stay informed",
                copy: "Read the reports online or get them delivered. Practical city news, simplified."
              }
            ].map((item, index) => {
              const Icon = item.icon
              return (
                <div
                  className={`flex flex-col items-center p-8 text-center ${index < 2 ? "border-b border-[hsl(var(--ch-primary))] md:border-b-0 md:border-r" : ""}`}
                  key={item.step}
                >
                  <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[hsl(var(--ch-primary))] bg-[hsl(var(--ch-surface))] font-serif text-2xl text-[hsl(var(--ch-primary))]">
                    {item.step}
                  </div>
                  <Icon className="mb-4 h-8 w-8 text-[hsl(var(--ch-primary))]" />
                  <h3 className="ch-headline-md mb-3 uppercase text-[hsl(var(--ch-primary))]">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-[hsl(var(--ch-muted))]">{item.copy}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </main>
  )
}
